import merge from "lodash.merge";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { FinancialBreakdownController } from "services/financialBreakdown/controller";
import { AccordionCard, Button, useAccordionWizard } from "stories";
import { useFetchState } from "utils/customHooks/useFetchState";
import { useToast } from "utils/customHooks/useToast";
import { v4 as uuidv4 } from "uuid";
import { LeaseTables } from "./leaseElements";
import { processVacancyData } from "./leaseElements/FinancialAnalysis/ReductionsTable";
import { SalesTables } from "./salesElements";
const deserializeVacancy = (assets) => {
  let result = {};
  for (const fullKey of Object.keys(assets)) {
    const keySplit = fullKey.split(">");
    let p = result;
    for (const key of keySplit) {
      if (!p[key]) {
        p[key] = {};
      }
      if (key !== keySplit[keySplit.length - 1]) p = p[key];
    }
    p[keySplit[keySplit.length - 1]] = assets[fullKey]
      ? parseFloat(assets[fullKey])
      : null;
  }
  return result;
};

const generateOperationExpenses = () => {
  const rates = [3.0, 0.75, 5.0, 0.5, 0.75, 3.0, 2.0, 13.0];
  return [
    "Management",
    "Admin & Marketing",
    "Payroll",
    "Water",
    "Utilities",
    "Maintenance & Repairs",
    "Insurance",
    "Property Taxes",
  ].map((title, i) => ({
    uid: uuidv4(),
    title,
    rate: rates[i],
    amount: "",
  }));
};
const errorMessage = "Please fill all of the inputs";
const validateBody = (body) => {
  if (typeof body !== "object") {
    if (typeof body === "string" && body.length === 0) return errorMessage;
    if (typeof body === "number" && isNaN(body)) return errorMessage;
    return;
  } else if (body === null) return errorMessage;

  for (let key of Object.keys(body)) {
    const error = validateBody(body[key]);
    if (error) return error;
  }
};

const financialBreakdown = FinancialBreakdownController();
const FinancialAnalysis = forwardRef((props, ref) => {
  const form = useForm();
  const { id: opportunityId } = useParams();
  const { close } = useAccordionWizard();
  const [fetchState, handleFetchState] = useFetchState();
  const [updateState, handleUpdateState] = useFetchState();
  const [error, setError] = useState(null);
  const [projectPurpose] = useWatch({
    name: ["project_purpose"],
    control: form.control,
  });
  useEffect(() => {
    handleFetchState(financialBreakdown.getFinancialAnalysis, opportunityId);
  }, []); // eslint-disable-line
  useImperativeHandle(ref, () => ({
    refetch() {
      handleFetchState(financialBreakdown.getFinancialAnalysis, opportunityId);
    },
  }));
  useEffect(() => {
    if (!fetchState.isError && !fetchState.isLoading && fetchState.data) {
      const data = fetchState.data;
      const vacancyTmp = processVacancyData(
        data.vacancy_rates || data.revenues.assets,
        ""
      );
      const vacancyAssets = processVacancyData(data.revenues.assets, "");
      data.vacancy_rates = merge(vacancyAssets, vacancyTmp);
      data.revenues.assets = processVacancyData(data.revenues.assets, "", true);
      data.reductions = fetchState.data.reductions || [];
      if (!data.operating_expenses)
        data.operating_expenses = generateOperationExpenses();
      form.reset(data);
    }
  }, [fetchState]); // eslint-disable-line

  const save = (values) => {
    setError(null);
    if (projectPurpose === "sale") {
      close();
    } else {
      const body = {
        cap_rate: values.cap_rate ? parseFloat(values.cap_rate) : undefined,
        operating_expenses: values.operating_expenses.map((row) => ({
          title: row.title,
          rate: parseFloat(row.rate),
        })),
        parking_vacancy_rate: values.parking_vacancy_rate
          ? parseFloat(values.parking_vacancy_rate)
          : undefined,
        storage_vacancy_rate: values.storage_vacancy_rate
          ? parseFloat(values.storage_vacancy_rate)
          : undefined,
        reductions: values.reductions.map((row) => ({
          title: row.title,
          amount: parseFloat(row.amount),
        })),
        vacancy_rates: deserializeVacancy(values.vacancy_rates),
      };
      const error = validateBody(body);
      if (error) return setError(error);
      body.id = opportunityId;
      handleUpdateState(financialBreakdown.updateFinancialAnalysis, body);
    }
  };
  const { showToast } = useToast();
  useEffect(() => {
    if (
      !updateState.isError &&
      !updateState.isLoading &&
      updateState.data === ""
    ) {
      showToast("Financial Analysis updated successfully", "success");
      close();
    }
  }, [updateState]); // eslint-disable-line

  return (
    <div className={"mt-4"} ref={ref}>
      <AccordionCard
        headerText={"Financial Analysis"}
        accordionKey="financial-analysis"
      >
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(save)}>
            {projectPurpose === "sale" && <SalesTables />}
            {projectPurpose === "lease" && <LeaseTables />}
            {error && <span className="text-red mt-3 d-block">{error}</span>}
            <Row className={"mt-5"}>
              <Col />
              <Col xs={2}>
                <Button label={"Save"} loading={updateState.isLoading} />
              </Col>
            </Row>
          </form>
        </FormProvider>
      </AccordionCard>
    </div>
  );
});

export default FinancialAnalysis;
